import React, { useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import Avatar from '@mui/material/Avatar';
import Person from '@mui/icons-material/Person';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import PropTypes from 'prop-types';
import { DialogContent } from '@mui/material';
import AvatarEdit from 'react-avatar-edit';
import Dialog from '../../../../components/Dialog';
import SubmitButton from '../../../../components/form/SubmitButton';

const DEFAULT_IMG_PATH = '/img/avatar-default.png';

function styles(theme) {
  return {
    root: {
      padding: theme.spacing(2),
    },
    center: {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    container: {
      position: 'relative',
      width: '100%',
      transition: '.5s ease',
      '&:hover': {
        '& $middle': {
          opacity: 0.6,
        },
        '& $wrapper': {
          opacity: 1,
        },
      },
    },
    text: {
      backgroundColor: '#404040',
      color: 'white',
      fontSize: '16px',
      height: '100px',
      width: '200px',
      padding: 'auto',
      borderBottomLeftRadius: 200 * 2,
      borderBottomRightRadius: 200 * 2,
      marginRight: 'auto',
      marginLeft: 'auto',
      marginTop: '100px',
    },
    middle: {
      height: '100%',
      width: '100%',
      transition: '.5s ease',
      opacity: 0,
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      verticalAlign: 'baseline',
    },
    wrapper: {
      height: '50%',
      width: '100%',
      transition: '.5s ease',
      opacity: 0,
      position: 'absolute',
      top: '75%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      verticalAlign: 'baseline',
    },
    small: {
      width: 80,
      height: 80,
      marginRight: 'auto',
      marginLeft: 'auto',
    },
    medium: {
      width: 140,
      height: 140,
      marginRight: 'auto',
      marginLeft: 'auto',
    },
    big: {
      width: 200,
      height: 200,
      marginRight: 'auto',
      marginLeft: 'auto',
      top: 0,
      backgroundPosition: 'center',
      opacity: 1,
      display: 'flex',
      transition: '.5s ease',
      backfaceVisibility: 'hidden',
    },
    smallIcon: { fontSize: 50 },
    mediumIcon: { fontSize: 100 },
    bigIcon: { fontSize: 160 },
  };
}

function AvatarChooser(props) {
  const { classes, id, size, value, onChange } = props;
  const [cropperImg, setCropperImage] = useState(null);
  const [newProfileImage, setNewProfileImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [preview, setPreview] = useState(false);
  const [src, setSrc] = useState(null);
  const valueIsStr = typeof value === 'string';
  const fileInput = React.createRef();

  React.useEffect(() => {
    if (!valueIsStr && value) {
      setSrc(URL.createObjectURL(value));
    }
    if (valueIsStr) {
      setSrc(value);
    }
    if (newProfileImage) {
      setSrc(newProfileImage);
    }
  }, [value, newProfileImage]);

  return (
    <div className={classes.root} title="Altere sua foto">
      <Dialog
        open={showCropper}
        id="cropperDialog"
        title="Cropper"
        onClose={() => setShowCropper(false)}
      >
        <DialogContent className={classes.center}>
          <AvatarEdit
            width={390}
            height={295}
            src={cropperImg || ''}
            onCrop={setPreview}
            label="Escolha um arquivo"
          />
          <div style={{ width: '100%', height: '16px' }} />
          <SubmitButton
            onClick={() => {
              onChange(preview);
              setNewProfileImage(preview);
              setShowCropper(false);
            }}
          >
            Salvar Foto
          </SubmitButton>
        </DialogContent>
      </Dialog>

      <input
        id={id}
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={(event) => {
          const input = fileInput.current;
          if (event && event.target.files[0]) {
            const currentFile = event.target.files[0];
            const fileItemReader = new FileReader();
            fileItemReader.addEventListener(
              'load',
              () => {
                setCropperImage(fileItemReader.result);
                setShowCropper(true);
                input.value = '';
              },
              false,
            );
            fileItemReader.readAsDataURL(currentFile);
          }
        }}
        style={{ display: 'none' }}
      />
      <label htmlFor={id} style={{ cursor: 'pointer' }}>
        <div className={classes.container}>
          <Avatar
            className={classes[size]}
            src={src || DEFAULT_IMG_PATH}
            onError={(e) => {
              if (!e.target.src.endsWith(DEFAULT_IMG_PATH)) {
                setSrc(DEFAULT_IMG_PATH);
              }
            }}
          >
            {value ? null : <Person className={classes[`${size}Icon`]} />}
          </Avatar>
          <div className={classes.middle}>
            <div className={classes.text} />
          </div>
          <div className={classes.wrapper}>
            <div style={{ lineHeight: '100px', color: 'white' }}>
              <PhotoCamera style={{ fontSize: 30 }} />
            </div>
          </div>
        </div>
      </label>
    </div>
  );
}

AvatarChooser.defaultProps = {
  size: 'big',
  value: null,
  onChange: null,
};

AvatarChooser.propTypes = {
  id: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'big']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default withStyles(styles)(AvatarChooser);
